// Third party library imports
import { inject, Injectable } from '@angular/core';
import {
    Auth,
    authState, sendPasswordResetEmail,
    createUserWithEmailAndPassword
} from '@angular/fire/auth';

// Zopup library imports
import { StorageKey, StorageService } from '@zopup/zop-shared';
import { ApiResponse, BizError } from '@zopup/zop-types';

// Internal project imports

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly afAuth = inject(Auth);
    protected readonly authState = authState(this.afAuth);

    constructor(
        private _storageService: StorageService
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    async forgotPassword(email: string): Promise<any> {
        return await sendPasswordResetEmail(this.afAuth, email)
            .then(() => {
                console.log('Password reset email sent, check your inbox.');
                return true;
            })
            .catch((error) => {
                console.log(error);
                return false;
            });
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Promise<ApiResponse<any>> {
        const response = new ApiResponse();
        return createUserWithEmailAndPassword(
            this.afAuth,
            user.email,
            user.password
        )
            .then(async (result) => {
                response.isSuccess = true;
                response.result = result.user;

                // Store the access token in the local storage
                const userData = JSON.parse(JSON.stringify(result.user));

                this._storageService.setItem<string>(
                    StorageKey.accessToken,
                    userData.stsTokenManager.accessToken
                );

                // Store the user on the local storage
                this._storageService.setItem<any>(
                    StorageKey.firebaseUser,
                    result.user
                );
                return response;
            })
            .catch((error) => {
                console.log('Error in firebase user creation!');
                console.log(error.message);
                return this.constructError(
                    response,
                    error.message,
                    'Error in firebase user creation!'
                );
            });
    }

    constructError(
        response: ApiResponse<any>,
        errorMsg: string,
        description: string
    ): ApiResponse<any> {
        try {
            response.isSuccess = false;
            response.errors = [];

            const errorObj = new BizError();
            errorObj.message = errorMsg;
            errorObj.description = description;

            response.errors.push(errorObj);
            return response;
        } catch (ex) {
            console.error('Error while constructring error response!');
            return new ApiResponse();
        }
    }
}
